/**
 * Tough-Bar Builder - Pricing Module
 * Cart and quantity management methods
 */
window.ToughBarBuilder = window.ToughBarBuilder || {};

window.ToughBarBuilder.pricing = {
  getActiveCartPersistenceScope() {
    const stateKeys = new Set();
    const handles = new Set();
    const items = Array.isArray(this.cartItems) ? this.cartItems : [];

    items.forEach(item => {
      const stateKey = this.resolveCartItemStateKey(item);
      if (stateKey) stateKeys.add(stateKey);

      const handle = this.resolveCartItemHandle(item);
      if (handle) handles.add(handle);
    });

    return { stateKeys, handles };
  },

  isPersistedCartEntryInScope(value, scope = null) {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return false;
    if (!scope) return true;

    const activeStateKeys = scope.stateKeys instanceof Set ? scope.stateKeys : new Set();
    const activeHandles = scope.handles instanceof Set ? scope.handles : new Set();
    if (activeStateKeys.size === 0 && activeHandles.size === 0) return false;

    if (activeStateKeys.has(normalizedValue)) return true;

    const handle = this.resolveCartItemHandle(normalizedValue);
    return !!handle && activeHandles.has(handle);
  },

  normalizePersistedCartQuantities(source = this.quantities, options = {}) {
    const scope = options.scope || null;
    const quantities = (source && typeof source === 'object' && !Array.isArray(source)) ? source : {};

    return Object.entries(quantities).reduce((normalized, [rawKey, rawQuantity]) => {
      const key = String(rawKey || '').trim();
      if (!key) return normalized;
      if (!this.isPersistedCartEntryInScope(key, scope)) return normalized;

      const quantity = Number.parseInt(rawQuantity, 10);
      if (!Number.isFinite(quantity) || quantity <= 1) return normalized;

      normalized[key] = quantity;
      return normalized;
    }, {});
  },

  normalizePersistedExcludedProducts(source = this.excludedProducts, options = {}) {
    const scope = options.scope || null;
    const entries = Array.isArray(source) ? source : [];
    const normalized = [];
    const seen = new Set();

    entries.forEach(rawValue => {
      const value = String(rawValue || '').trim();
      if (!value) return;
      if (!this.isPersistedCartEntryInScope(value, scope)) return;
      if (seen.has(value)) return;

      seen.add(value);
      normalized.push(value);
    });

    return normalized;
  },

  syncPersistedCartStateIntoContext(options = {}) {
    if (!this.context || typeof this.context !== 'object') {
      this.context = {};
    }

    const scope = options.scope || this.getActiveCartPersistenceScope();
    const quantities = options.quantities === undefined ? this.quantities : options.quantities;
    const excludedProducts = options.excludedProducts === undefined ? this.excludedProducts : options.excludedProducts;

    this.context.cart_quantities = this.normalizePersistedCartQuantities(quantities, { scope });
    this.context.excluded_products = this.normalizePersistedExcludedProducts(excludedProducts, { scope });
  },

  restorePersistedCartStateFromContext(context = this.context) {
    const scope = this.getActiveCartPersistenceScope();
    const cartQuantities = context?.cart_quantities;
    const excludedProducts = context?.excluded_products;
    const normalizedQuantities = this.normalizePersistedCartQuantities(cartQuantities, { scope });
    const normalizedExcludedProducts = this.normalizePersistedExcludedProducts(excludedProducts, { scope });

    this.quantities = normalizedQuantities;
    this.excludedProducts = normalizedExcludedProducts;
    this.syncPersistedCartStateIntoContext({
      scope,
      quantities: normalizedQuantities,
      excludedProducts: normalizedExcludedProducts
    });
  },

  prunePersistedCartStateToActiveItems() {
    const scope = this.getActiveCartPersistenceScope();
    const normalizedQuantities = this.normalizePersistedCartQuantities(this.quantities, { scope });
    const normalizedExcludedProducts = this.normalizePersistedExcludedProducts(this.excludedProducts, { scope });

    this.quantities = normalizedQuantities;
    this.excludedProducts = normalizedExcludedProducts;
    this.syncPersistedCartStateIntoContext({
      scope,
      quantities: normalizedQuantities,
      excludedProducts: normalizedExcludedProducts
    });
  },

  scheduleCartStateSnapshotRefresh() {
    if (this.step < 2) return;
    if (typeof this.syncSnapshotWithCurrentState !== 'function') return;
    if (this.snapshotSuppressUrlUpdates) return;

    if (this._cartStateSnapshotRefreshTimer) {
      clearTimeout(this._cartStateSnapshotRefreshTimer);
    }

    this._cartStateSnapshotRefreshTimer = setTimeout(async () => {
      this._cartStateSnapshotRefreshTimer = null;
      if (this._cartStateSnapshotRefreshInFlight) {
        this._cartStateSnapshotRefreshPending = true;
        return;
      }

      this._cartStateSnapshotRefreshInFlight = true;

      try {
        await this.syncSnapshotWithCurrentState(true);
      } catch (snapshotError) {
        console.warn('[Tough-Bar Pricing] Snapshot refresh failed:', snapshotError);
      } finally {
        this._cartStateSnapshotRefreshInFlight = false;
        if (this._cartStateSnapshotRefreshPending) {
          this._cartStateSnapshotRefreshPending = false;
          this.scheduleCartStateSnapshotRefresh();
        }
      }
    }, 250);
  },

  resolveCartItemHandle(itemOrKey) {
    if (itemOrKey && typeof itemOrKey === 'object') {
      const handle = itemOrKey.handle || itemOrKey.product?.handle;
      return String(handle || '').trim();
    }

    const rawValue = String(itemOrKey || '').trim();
    if (!rawValue) return '';
    if (!rawValue.includes('|')) return rawValue;

    const parts = rawValue.split('|');
    return String(parts[1] || rawValue).trim();
  },

  buildCartItemStateKey(item) {
    if (!item || typeof item !== 'object') return '';

    const handle = this.resolveCartItemHandle(item);
    if (!handle) return '';

    const section = String(item.section || 'item').trim() || 'item';
    const accessoryId = String(item.accessoryId || '').trim() || '-';
    const deviceType = String(item.deviceType || '').trim() || '-';
    const sourceKitId = String(item.sourceKitId || '').trim() || '-';

    return [section, handle, accessoryId, deviceType, sourceKitId].join('|');
  },

  resolveCartItemStateKey(itemOrKey) {
    if (itemOrKey && typeof itemOrKey === 'object') {
      const explicitStateKey = String(itemOrKey.stateKey || '').trim();
      if (explicitStateKey) return explicitStateKey;

      const builtStateKey = this.buildCartItemStateKey(itemOrKey);
      if (builtStateKey) return builtStateKey;
    }

    return String(itemOrKey || '').trim();
  },

  getMatchingCartStateKeys(itemOrKey, sourceValues = []) {
    const stateKey = this.resolveCartItemStateKey(itemOrKey);
    const handle = this.resolveCartItemHandle(itemOrKey);
    const isItemObject = !!itemOrKey && typeof itemOrKey === 'object';
    const matches = new Set();

    if (stateKey) matches.add(stateKey);
    if (!handle || !Array.isArray(sourceValues)) return Array.from(matches);

    sourceValues.forEach(value => {
      const normalizedValue = String(value || '').trim();
      if (!normalizedValue) return;
      if (isItemObject) {
        if (normalizedValue === handle) {
          matches.add(normalizedValue);
        }
        return;
      }

      if (normalizedValue === handle || this.resolveCartItemHandle(normalizedValue) === handle) {
        matches.add(normalizedValue);
      }
    });

    return Array.from(matches);
  },

  isExcluded(itemOrKey) {
    const matches = this.getMatchingCartStateKeys(itemOrKey, this.excludedProducts);
    return matches.some(match => this.excludedProducts.includes(match));
  },

  excludeFromKit(itemOrKey) {
    const stateKey = this.resolveCartItemStateKey(itemOrKey);
    if (!stateKey) return;

    const handle = this.resolveCartItemHandle(itemOrKey);
    const isItemObject = !!itemOrKey && typeof itemOrKey === 'object';
    const next = Array.isArray(this.excludedProducts) ? [...this.excludedProducts] : [];
    const filtered = next.filter(entry => {
      const normalizedEntry = String(entry || '').trim();
      if (!normalizedEntry) return false;
      if (normalizedEntry === stateKey) return false;
      if (!handle) return true;

      if (!isItemObject) {
        return this.resolveCartItemHandle(normalizedEntry) !== handle;
      }

      // Object-based callers should preserve sibling line-item exclusions that
      // happen to share the same handle while still replacing any broad legacy
      // handle-only exclusion entry.
      return normalizedEntry !== handle;
    });

    filtered.push(stateKey);
    this.excludedProducts = filtered;
    this.syncPersistedCartStateIntoContext();
    this.scheduleCartStateSnapshotRefresh();
  },

  addBackToKit(itemOrKey) {
    const stateKey = this.resolveCartItemStateKey(itemOrKey);
    const handle = this.resolveCartItemHandle(itemOrKey);

    const nextExcludedProducts = (this.excludedProducts || []).filter(entry => {
      if (stateKey && entry === stateKey) return false;
      if (!handle) return true;

      // String-based callers from legacy paths intentionally clear all entries for the handle.
      if (!itemOrKey || typeof itemOrKey !== 'object') {
        return this.resolveCartItemHandle(entry) !== handle;
      }

      return entry !== handle;
    });

    this.excludedProducts = nextExcludedProducts;
    this.syncPersistedCartStateIntoContext();
    this.scheduleCartStateSnapshotRefresh();
  },

  getQuantity(itemOrKey) {
    const stateKey = this.resolveCartItemStateKey(itemOrKey);
    if (!stateKey) return 1;
    if (Object.prototype.hasOwnProperty.call(this.quantities, stateKey)) {
      return this.quantities[stateKey] || 1;
    }

    const handle = this.resolveCartItemHandle(itemOrKey);
    if (handle && Object.prototype.hasOwnProperty.call(this.quantities, handle)) {
      return this.quantities[handle] || 1;
    }

    return 1;
  },

  getNextQuantitiesState() {
    return (this.quantities && typeof this.quantities === 'object' && !Array.isArray(this.quantities))
      ? { ...this.quantities }
      : {};
  },

  updateQuantity(itemOrKey, delta) {
    const stateKey = this.resolveCartItemStateKey(itemOrKey);
    if (!stateKey) return;

    const current = this.getQuantity(itemOrKey);
    const newQty = Math.max(1, current + delta);
    const nextQuantities = this.getNextQuantitiesState();
    if (newQty <= 1) {
      delete nextQuantities[stateKey];
    } else {
      nextQuantities[stateKey] = newQty;
    }

    const handle = this.resolveCartItemHandle(itemOrKey);
    if (handle && handle !== stateKey && Object.prototype.hasOwnProperty.call(nextQuantities, handle)) {
      delete nextQuantities[handle];
    }

    this.quantities = nextQuantities;
    this.syncPersistedCartStateIntoContext();
    this.scheduleCartStateSnapshotRefresh();
  },

  isRemoved(itemOrKey) {
    const matches = this.getMatchingCartStateKeys(itemOrKey, this.removedProducts);
    return matches.some(match => this.removedProducts.includes(match));
  },

  removeProduct(itemOrKey) {
    const stateKey = this.resolveCartItemStateKey(itemOrKey);
    if (!stateKey) return;

    const handle = this.resolveCartItemHandle(itemOrKey);
    const nextQuantities = this.getNextQuantitiesState();
    const next = Array.isArray(this.removedProducts) ? [...this.removedProducts] : [];
    const filtered = handle
      ? next.filter(entry => this.resolveCartItemHandle(entry) !== handle)
      : next.filter(entry => entry !== stateKey);

    filtered.push(stateKey);
    this.removedProducts = filtered;
    delete nextQuantities[stateKey];

    if (handle && handle !== stateKey && Object.prototype.hasOwnProperty.call(nextQuantities, handle)) {
      delete nextQuantities[handle];
    }

    this.quantities = nextQuantities;
  },

  restoreProduct(itemOrKey) {
    const stateKey = this.resolveCartItemStateKey(itemOrKey);
    const handle = this.resolveCartItemHandle(itemOrKey);

    this.removedProducts = (this.removedProducts || []).filter(entry => {
      if (stateKey && entry === stateKey) return false;
      if (!handle) return true;
      return this.resolveCartItemHandle(entry) !== handle;
    });
  },

  getToughBarBuilderCartProperties() {
    const properties = {};
    const currentUrl = typeof window !== 'undefined' ? window.location?.href : '';

    if (currentUrl) {
      properties._Tough_Bar_Builder_URL = currentUrl;
    }

    const builderContext = this.context || {};
    for (const [key, value] of Object.entries(builderContext)) {
      const propertyValue = this.normalizeToughBarBuilderCartPropertyValue(value);
      if (!propertyValue) continue;

      const propertyKey = '_Tough_Bar_Builder_' + key.replace(/ /g, '_');
      properties[propertyKey] = propertyValue;
    }

    return properties;
  },

  normalizeToughBarBuilderCartPropertyValue(value) {
    if (value === null || value === undefined || value === false) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      if (value.length === 0) return '';
      return JSON.stringify(value);
    }
    if (typeof value === 'object') {
      if (Object.keys(value).length === 0) return '';
      return JSON.stringify(value);
    }

    return '';
  },

  async addAllToCart() {
    if (this.availableCartItems.length === 0) {
      this.error = this.cartItems.length === 0
        ? 'Your kit is empty. Add products before continuing.'
        : 'There are no available items in your kit to add to cart.';
      return;
    }

    const skippedHandles = [];
    const cartProperties = this.getToughBarBuilderCartProperties();
    const itemsByVariantId = this.availableCartItems.reduce((acc, item) => {
      const variantId = item?.product?.variants?.[0]?.id;
      if (!variantId) {
        skippedHandles.push(item?.handle || 'unknown');
        return acc;
      }

      const existing = acc.get(variantId) || { id: variantId, quantity: 0, properties: { ...cartProperties } };
      existing.quantity += Number(item.quantity || 1);
      acc.set(variantId, existing);

      return acc;
    }, new Map());
    const items = Array.from(itemsByVariantId.values());

    if (items.length === 0) {
      console.warn('[Tough-Bar Pricing] No cart items with valid variants.', skippedHandles);
      this.error = 'Unable to add items to cart. One or more products are missing purchasable variants.';
      return;
    }

    if (skippedHandles.length > 0) {
      console.warn('[Tough-Bar Pricing] Skipping items without variants:', skippedHandles);
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        throw new Error(`Cart add failed (${res.status})`);
      }

      window.location.href = '/cart';
    } catch (err) {
      console.error('Failed to add to cart:', err);
      this.error = 'Failed to add items to cart. Please try again.';
    }
  },
};
